<template>
  <!-- Figma F2 / F4 / F6 "Quote" card on Job Details (after the evaluation is submitted).
       API (G-18): GET bookings/diagnosis/summary/quote — read-only; hidden when the API fails or there is no quote. -->
  <div v-if="hasQuote"
    class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white font-roboto">
    <div class="flex flex-wrap items-center justify-between gap-2 pb-[10px]">
      <div class="flex items-center gap-1">
        <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
        <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
        <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Quote-<span v-if="quote.booking_id"> {{ quote.booking_id }}</span></p>
      </div>
      <button type="button"
        class="h-[32px] px-[14px] rounded-[6px] border-1 border-[#7695FF] bg-[#fff] text-[13px] text-[#7695FF] hover:bg-[#EEF2FF]"
        @click="openFullQuote">View full quote</button>
    </div>

    <!-- Figma F6 (approved): a status pill per section; a section the consumer left some rows out of lists them, with
         "n approved · n declined" -->
    <div v-for="row in rows" :key="row.heading" class="py-[12px] border-b-1 border-[#EFEFEF] text-[13px]">
      <div class="flex items-center justify-between gap-3">
        <span class="flex flex-wrap items-center gap-[8px] min-w-0">
          <span class="text-[#555555]">{{ row.heading }}</span>
          <span v-if="quote.is_approved && row.expanded"
            class="px-[8px] py-[2px] rounded-[6px] bg-[#F1F2F5] text-[11px] text-[#666666]">{{ row.approvedCount }} approved · {{ row.declinedCount }} declined</span>
          <span v-else-if="quote.is_approved"
            class="px-[8px] py-[2px] rounded-[6px] bg-[#E7F7F0] text-[11px] font-medium text-[#1FA971]">Approved</span>
        </span>
        <span class="font-medium text-[#333333] whitespace-nowrap">{{ row.total }}</span>
      </div>
      <div v-if="quote.is_approved && row.expanded" class="mt-[10px] rounded-[8px] border-1 border-[#EFEFEF] bg-[#F9FAFB]">
        <div v-for="(item, i) in row.items" :key="i" class="px-[12px] py-[10px]" :class="i ? 'border-t-1 border-[#EFEFEF]' : ''">
          <div class="flex items-center justify-between gap-3">
            <span class="flex flex-wrap items-center gap-[8px] min-w-0">
              <span class="w-[7px] h-[7px] rounded-full flex-shrink-0" :class="item.declined ? 'bg-[#EE5851]' : 'bg-[#1FA971]'" />
              <span :class="item.declined ? 'text-[#A8AEB8]' : 'text-[#333333]'">{{ item.name }}</span>
              <span v-if="item.declined"
                class="px-[8px] py-[1px] rounded-[6px] bg-[#FDECEC] text-[11px] font-medium text-[#D7385E]">Declined</span>
              <span v-else class="px-[8px] py-[1px] rounded-[6px] bg-[#E7F7F0] text-[11px] font-medium text-[#1FA971]">Approved</span>
            </span>
            <span class="whitespace-nowrap" :class="item.declined ? 'text-[#A8AEB8] line-through' : 'text-[#333333]'">{{ item.total }}</span>
          </div>
          <p v-if="item.declined" class="m-0 mt-[2px] pl-[15px] text-[12px] text-[#8A9099]">Not charged — kept as a follow-up opportunity</p>
          <p v-else-if="item.warranty" class="m-0 mt-[2px] pl-[15px] text-[12px] text-[#1FA971]">Covered by labor warranty</p>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between gap-2 mt-[12px] px-[15px] py-[14px] rounded-[8px] bg-[#F3F4F7]">
      <span class="text-[13px] font-medium text-[#333333]">Total Amount</span>
      <span class="text-[18px] laptop:text-[20px] font-bold text-[#0E2F7E]">{{ quote.total_text || money(quote.total)
        }}</span>
    </div>

    <!-- F4 / F6: approved. "View signature" has no source yet (the quote API sends no signature image / link) -->
    <div v-if="quote.is_approved"
      class="flex items-center gap-[8px] mt-[12px] px-[15px] py-[12px] rounded-[8px] border-1 border-[#BFE7D5] bg-[#F0FAF5] text-[14px] text-[#157A52]">
      <span class="w-[26px] h-[26px] rounded-full bg-[#1FA971] flex items-center justify-center flex-shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 64 64">
          <path d="M0 0h64v64H0z" fill="none" />
          <path fill="#fff" d="M56 2L18.8 42.909L8 34.729H2L18.8 62L62 2z" />
        </svg>
      </span>
      <span>Signed approval captured<span v-if="declinedCount"> · {{ declinedCount }} {{ declinedCount === 1 ? 'item' : 'items' }} not approved, saved as a follow-up opportunity</span></span>
    </div>

    <!-- F2: awaiting approval — send the consumer's quote link again -->
    <div v-else-if="shareUrl" class="flex flex-wrap items-center justify-between gap-[10px] mt-[15px]">
      <span class="text-[13px] text-[#555555]">Send the quote again:</span>
      <div class="flex flex-wrap items-center gap-[8px]">
        <a v-if="consumer?.email" :href="mailLink"
          class="flex items-center gap-[6px] h-[32px] px-[12px] rounded-[6px] border-1 border-[#E2E5EC] bg-[#fff] text-[12px] text-[#333333] no-underline hover:bg-[#F7F8FA]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="#333333" stroke-width="1.8" />
            <path d="M4 7l8 6 8-6" stroke="#333333" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Email
        </a>
        <a v-if="consumer?.phone" :href="smsLink"
          class="flex items-center gap-[6px] h-[32px] px-[12px] rounded-[6px] border-1 border-[#E2E5EC] bg-[#fff] text-[12px] text-[#333333] no-underline hover:bg-[#F7F8FA]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 5h16v11H9l-5 4V5z" stroke="#333333" stroke-width="1.8" stroke-linejoin="round" />
          </svg>
          SMS
        </a>
        <button type="button"
          class="flex items-center gap-[6px] h-[32px] px-[12px] rounded-[6px] border-1 border-[#E2E5EC] bg-[#fff] text-[12px] text-[#333333] hover:bg-[#F7F8FA]"
          @click="copyLink">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="8" y="8" width="12" height="12" rx="2" stroke="#333333" stroke-width="1.8" />
            <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" stroke="#333333" stroke-width="1.8" />
          </svg>
          Copy link
        </button>
      </div>
    </div>

    <!-- full breakdown (same card as Review & Submit) -->
    <!-- same dialog + height as the line item breakdown (summary_detail_card .lineItemModal) -->
    <div ref="fullQuoteModal" class="modal fade fxModal fullQuoteModal" tabindex="-1" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[500px] tablet:!max-w-[500px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <div class="modal-content">
          <div class="modal-body !p-[15px]">
            <FxModalHeader title="Quote" />
            <summary_detail_card v-if="showFull" :details="quote" class="mt-[15px]" />
            <!-- same total as the card on Job Details -->
            <div v-if="showFull"
              class="flex items-center justify-between gap-2 mt-[12px] px-[15px] py-[14px] rounded-[8px] bg-[#F3F4F7]">
              <span class="text-[13px] font-medium text-[#333333]">Total Amount</span>
              <span class="text-[18px] laptop:text-[20px] font-bold text-[#0E2F7E]">{{ quote.total_text || money(quote.total) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "./FxModalHeader.vue";
import summary_detail_card from "./summary_detail_card.vue";
const toast = useToast();

export default {
  components: { FxModalHeader, summary_detail_card },
  props: {
    bookingId: { type: [String, Number], required: true },
    // consumer's quote link (qrCode/job-detail → jobStateUrl)
    shareUrl: { type: String, default: '' },
    // jobDetail.consumer: { phone, email }
    consumer: { type: Object, default: null },
  },
  data() {
    return { quote: null, showFull: false };
  },
  computed: {
    hasQuote() {
      return Array.isArray(this.quote?.items) && this.quote.items.length > 0;
    },
    // one row per section (Required Repair, Recommended…, Misc. Items, Service Fee); on an approved quote the total counts
    // only what the consumer approved (is_selected 0 = left out, G-72)
    rows() {
      return (this.quote?.items || []).map(section => {
        const items = (section.data || []).map(item => ({
          name: String(item.option || item.title || '').replace(/<[^>]*>/g, '').trim() || '-',
          declined: !!this.quote.is_approved && item.is_selected == 0,
          warranty: item.is_service_warranty == 1,
          cost: Number(item.total_cost || 0),
        }));
        const declinedCount = items.filter(i => i.declined).length;
        return {
          heading: section.heading,
          total: this.money(items.filter(i => !i.declined).reduce((sum, i) => sum + i.cost, 0)),
          items: items.map(i => ({ ...i, total: this.money(i.cost) })),
          declinedCount,
          approvedCount: items.length - declinedCount,
          expanded: declinedCount > 0,
        };
      });
    },
    declinedCount() {
      return this.rows.reduce((sum, r) => sum + r.declinedCount, 0);
    },
    message() {
      return 'Here is your quote: ' + this.shareUrl;
    },
    mailLink() {
      return 'mailto:' + this.consumer.email + '?subject=' + encodeURIComponent('Your quote') + '&body=' + encodeURIComponent(this.message);
    },
    // digits only: "(205) 879-5122" in an sms: URI is invalid and hung Chrome on Android; iOS wants "&body="
    smsLink() {
      const phone = String(this.consumer.phone).replace(/[^\d+]/g, '');
      const sep = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? '&' : '?';
      return 'sms:' + phone + sep + 'body=' + encodeURIComponent(this.message);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    if (this.modal) this.modal.hide();
  },
  methods: {
    money(value) {
      const amount = Number(value || 0);
      return (amount < 0 ? '-$' : '$') + Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async load() {
      try {
        const res = await axios.get("bookings/diagnosis/summary/quote", { params: { booking_id: this.bookingId } });
        this.quote = res.data?.status ? res.data.body : null;
      } catch (e) {
        this.quote = null;
      }
    },
    openFullQuote() {
      this.showFull = true;
      this.$nextTick(() => {
        this.modal = this.modal || new Modal(this.$refs.fullQuoteModal);
        this.modal.show();
      });
    },
    copyLink() {
      navigator.clipboard.writeText(this.shareUrl);
      toast.success("Link copied.");
    },
  },
};
</script>
